import { QueryClient } from '@tanstack/react-query';
import { isApiError } from './api';

/**
 * One QueryClient for the app. Conventions for query keys (keep them flat and predictable so
 * pages can invalidate each other):
 *
 *   ['positions', 'list', filters]        ['positions', 'detail', id]
 *   ['candidates', 'list', filters]       ['candidates', 'detail', id]
 *   ['submissions', 'list', filters]      ['submissions', 'detail', id]
 *   ['partners', 'list', filters]         ['partners', 'detail', id]
 *   ['activity', entityType, entityId]    ['follow-ups', entityType, entityId]
 *   ['files', entityType, entityId]       ['views', entityType]
 *   ['dashboard']  ['settings']  ['options']  ['search', q]  ['auth', 'me']
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry(failureCount, error) {
          // Never retry client errors (401 is handled by the api client, 4xx are final).
          if (isApiError(error) && error.status > 0 && error.status < 500) return false;
          return failureCount < 2;
        },
      },
      mutations: {
        retry: false,
      },
    },
  });
}

export const queryClient = createQueryClient();

/** Query key helpers so pages and generic components agree on the shape. */
export const queryKeys = {
  list: (entity: string, filters?: unknown) => ['' + entity, 'list', filters ?? {}] as const,
  detail: (entity: string, id: string) => ['' + entity, 'detail', id] as const,
  activity: (entityType: string, entityId: string) => ['activity', entityType, entityId] as const,
  followUps: (entityType: string, entityId: string) => ['follow-ups', entityType, entityId] as const,
  files: (entityType: string, entityId: string) => ['files', entityType, entityId] as const,
  views: (entityType: string) => ['views', entityType] as const,
  search: (q: string) => ['search', q] as const,
  me: ['auth', 'me'] as const,
  dashboard: ['dashboard'] as const,
  settings: ['settings'] as const,
  options: ['options'] as const,
};
